import { ChangeEvent, useEffect, useState } from "react";
import StatusBadge from "./StatusBadge";
import StageOutOfScopeMessage from "./StageOutOfScopeMessage";
import IntegrationCredentialSections, {
  CredentialFormFields,
} from "./IntegrationCredentialSections";
import {
  AUTH_TYPE,
  AuthTypeId,
  ConnectionTestResult,
  DEFAULT_AUTH_TYPE,
  HttpMethodId,
  STAGE_DESCRIPTIONS,
  STAGE_META,
  ServiceProvider,
  StageConfig,
} from "../../../utils/integrationConfigData";
import {
  AuthTypeApi,
  ConfigurationTypeApi,
  mapAuthTypeIdToApiId,
  mapIntegrationToStageConfig,
} from "../types/providerIntegrationtypes";
import { isTestSuccess } from "../../../constants/integrationKeys";
import {
  formatTestResultMessage,
  saveStageConfig,
} from "../../../utils/integrationApiHelpers";
import { useAuditUserId } from "../../../hooks/useAuditUserId";
import { useStageConfigLoader, secretOk } from "../../../utils/useStageConfigLoader";
import { KeyValueItem } from "../../../utils/keyValueItems";
import { ProviderIntegrationApi } from "../../../service/Api/ProviderIntegrationApi";
interface StageCrdFormProps {
  provider: ServiceProvider;
  stage?: StageConfig;
  showInfoBanner?: boolean;
  authTypes?: AuthTypeApi[];
  onProvidersChanged?: () => void;
}
type FormState = CredentialFormFields;
const buildFormState = (stage?: StageConfig): FormState => ({
  endpoint: stage?.endpoint || "",
  authType: stage?.authType || DEFAULT_AUTH_TYPE,
  httpMethod: stage?.httpMethod || "GET",
  requestUrls: stage?.requestUrls || [],
  headers: stage?.headers || [],
  credentialHeaders: stage?.credentialHeaders || [],
  queryParams: stage?.queryParams || [],
  requestBody: stage?.requestBody || [],
  bearerToken: stage?.bearerToken || "",
  username: stage?.username || "",
  password: stage?.password || "",
  headerName: stage?.headerName || "",
  apiKey: stage?.apiKey || "",
});

const StageCrdForm = ({
  provider,
  stage,
  showInfoBanner = true,
  authTypes = [],
  onProvidersChanged,
}: StageCrdFormProps) => {
  const meta = STAGE_META.crd;
  const description = stage?.description || STAGE_DESCRIPTIONS.crd;
  const auditUserId = useAuditUserId();

  const [configurationTypes, setConfigurationTypes] = useState<ConfigurationTypeApi[]>([]);
  const [selectedConfigurationTypeId, setSelectedConfigurationTypeId] = useState<
    number | null
  >(null);
  const { loadedStage, setLoadedStage, loadError, isLoading, reloadForAuthType } =
    useStageConfigLoader(
      provider.id,
      "crd",
      authTypes,
      stage,
      selectedConfigurationTypeId,
    );
  const [form, setForm] = useState<FormState>(() => buildFormState(stage));
  const [testResult, setTestResult] = useState<ConnectionTestResult | null>(
    stage?.testResult || null,
  );
  const [isSaving, setIsSaving] = useState(false);
  const hasExistingConfig = Boolean(loadedStage?.configurationId);

  useEffect(() => {
    let cancelled = false;
    ProviderIntegrationApi.getConfigurationTypes("CRD")
      .then((types) => {
        if (cancelled) return;
        setConfigurationTypes(types);
        if (types.length && selectedConfigurationTypeId == null) {
          setSelectedConfigurationTypeId(types[0].id);
        }
      })
      .catch(() => {
        if (!cancelled) setConfigurationTypes([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setForm((prev) => ({
      ...buildFormState(loadedStage),
      authType: loadedStage?.authType || prev.authType,
    }));
    setTestResult(loadedStage?.testResult || null);
  }, [loadedStage]);

  useEffect(() => {
    if (loadError) {
      setTestResult({ message: loadError, status: "error" });
    }
  }, [loadError]);

  const handleConfigurationTypeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const nextId = Number(event.target.value);
    setSelectedConfigurationTypeId(Number.isFinite(nextId) ? nextId : null);
    setTestResult(null);
  };

  const handleChange =
    (field: keyof FormState) =>
      (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const value = event.target.value;
        setForm((prev) => ({
          ...prev,
          [field]:
            field === "authType"
              ? (value as AuthTypeId)
              : field === "httpMethod"
                ? (value as HttpMethodId)
                : value,
        }));
        setTestResult(null);
        if (field === "authType") {
          const authTypeId = mapAuthTypeIdToApiId(value as AuthTypeId, authTypes);
          if (authTypeId) reloadForAuthType(authTypeId);
        }
      };

  const handleListChange = (
    field: "requestUrls" | "headers" | "credentialHeaders" | "queryParams" | "requestBody",
    items: KeyValueItem[],
  ) => {
    setForm((prev) => ({ ...prev, [field]: items }));
    setTestResult(null);
  };
  const getValidationError = (): string | null => {
    if (!selectedConfigurationTypeId) {
      return "Failed · Missing CRD endpoint type · 0ms";
    }
    if (!form.endpoint.trim()) {
      return "Failed · Missing endpoint · 0ms";
    }
    switch (form.authType) {
      case AUTH_TYPE.BearerToken:
        if (
          !secretOk(form.bearerToken, hasExistingConfig, Boolean(form.bearerToken))
        ) {
          return "Failed · Missing bearer token · 0ms";
        }
        break;
      case AUTH_TYPE.OAuth2ClientCredentials:
        // Credential field names are client-defined (dynamic key/value lists).
        break;
      case AUTH_TYPE.Basic:
        if (!form.username.trim() || !form.password.trim()) {
          return "Failed · Missing username/password · 0ms";
        }
        break;
      case AUTH_TYPE.ApiKeyHeader:
        if (
          !form.headerName.trim() ||
          (!form.apiKey.trim() &&
            form.headers.length === 0 &&
            form.credentialHeaders.length === 0)
        ) {
          return "Failed · Missing API key header · 0ms";
        }
        break;
      default:
        break;
    }
    return null;
  };

  const handleSave = async () => {
    const validationError = getValidationError();
    if (validationError) {
      setTestResult({ message: validationError, status: "error" });
      return;
    }
    const providerId = Number(provider.id);
    if (!providerId || !selectedConfigurationTypeId) return;
    setIsSaving(true);
    try {
      const result = await saveStageConfig(
        providerId,
        "crd",
        form,
        authTypes,
        auditUserId,
        selectedConfigurationTypeId,
      );
      setTestResult({
        message: formatTestResultMessage(result),
        status: isTestSuccess(result.testResult?.testStatus) ? "success" : "error",
      });
      setLoadedStage({
        ...mapIntegrationToStageConfig(result),
        configurationId: result.id,
      });
      onProvidersChanged?.();
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Failed · Save failed · 0ms";
      setTestResult({
        message: message.startsWith("Failed")
          ? message
          : `Failed · ${message} · 0ms`,
        status: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const fieldId = (name: string) => `crd-${name}`;
  const displayStatus = loadedStage?.status || stage?.status || "notSet";
  const isNotSet = displayStatus === "notSet";

  return (
    <div className="ic-stage-form-panel">
      {showInfoBanner && (
        <div className="ic-info-banner" role="note">
          <i className="fh_info_fill" aria-hidden="true"></i>
          <span>
            Configure CRD endpoints for this vendor, then Test &amp; Save
            Configuration.
          </span>
        </div>
      )}
      <div className="ic-stage-form-shell">
        <div className="ic-stage-form-content">
          <header className="ic-stage-form-header">
            <div className="ic-stage-form-title-row">
              <span className="ic-stats-icon">
                <i className={meta.icon}></i>
              </span>
              <div className="ic-stage-form-heading-text">
                <div className="ic-stage-form-title-line">
                  <h2 className="ic-stage-form-title">{meta.label}</h2>
                  <StatusBadge status={displayStatus} />
                </div>
                <p className="ic-stage-form-desc mb-0">{description}</p>
              </div>
            </div>
          </header>
          {isLoading && (
            <div className="ic-inline-message" role="status">
              Loading CRD configuration...
            </div>
          )}
          {isNotSet && !isLoading && (
            <StageOutOfScopeMessage stageLabel={meta.label} />
          )}
          <section className="ic-stage-form-section">
            <h3 className="ic-stage-form-section-title">CRD endpoint</h3>
            <div className="ic-stage-form-field">
              <label className="ic-stage-form-label" htmlFor={fieldId("endpoint-type")}>
                CRD endpoint
              </label>
              <select
                id={fieldId("endpoint-type")}
                className="form-select form-select-sm ic-stage-form-input ic-stage-form-select"
                value={selectedConfigurationTypeId ?? ""}
                onChange={handleConfigurationTypeChange}
                disabled={!configurationTypes.length}
              >
                {configurationTypes.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.displayName}
                  </option>
                ))}
              </select>
            </div>
          </section>
          <IntegrationCredentialSections
            form={form}
            fieldId={fieldId}
            onFieldChange={handleChange}
            onListChange={handleListChange}
            connectionActive={
              loadedStage?.configurationId != null
                ? !Boolean(loadedStage.isDeleted)
                : undefined
            }
          />
          <footer className="ic-stage-form-footer">
            <button
              type="button"
              className="btn btn-primary btn-sm ic-test-connection-btn"
              onClick={handleSave}
              disabled={isSaving || isLoading || !selectedConfigurationTypeId}
            >
              {isSaving ? (
                <>
                  <span className="ic-test-spinner" aria-hidden="true" />
                  Testing &amp; saving...
                </>
              ) : (
                "Test & Save Configuration"
              )}
            </button>
            {testResult ? (
              <div
                className={`ic-test-result ic-test-result-${testResult.status}`}
                role="status"
              >
                <i
                  className={
                    testResult.status === "error"
                      ? "fh_error_line"
                      : "fh_check_circle_line"
                  }
                  aria-hidden="true"
                ></i>
                <span>{testResult.message}</span>
              </div>
            ) : null}
          </footer>
        </div>
      </div>
    </div>
  );
};
export default StageCrdForm;